import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { WI_PATH } from "./wisconsinPath";
import wiCities from "./wiCities.json";

// Projection matches wisconsinPath.js generator:
// x = ((lng+92.9)/6.15)*500, y = ((47.1−lat)/4.7)*500
const project = (lat, lng) => ({
  x: ((lng + 92.9) / 6.15) * 500,
  y: ((47.1 - lat) / 4.7) * 500,
});

// deterministic hash → small jitter so many schools in same city don't stack
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
};
const jitter = (id) => {
  const h = hash(id || "");
  const a = (h & 0xff) / 255 - 0.5;
  const b = ((h >> 8) & 0xff) / 255 - 0.5;
  return { dx: a * 10, dy: b * 10 };
};

export default function WisconsinMap({ schoolsAddedToday = 0, fundsRaisedWeek = 0 }) {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [hotspots, setHotspots] = useState([]);
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    api.schools({ limit: 1000 }).then(({ data }) => setSchools(data || [])).catch(() => {});
    api.promCupLeaderboard(3).then(({ data }) => setHotspots(data || [])).catch(() => {});
  }, []);

  const dots = useMemo(() => {
    const out = [];
    for (const s of schools) {
      const coords = wiCities[s.city];
      if (!coords) continue;
      const [lat, lng] = coords;
      const p = project(lat, lng);
      const j = jitter(s.id);
      out.push({ id: s.id, slug: s.slug, name: s.name, city: s.city, x: p.x + j.dx, y: p.y + j.dy });
    }
    return out;
  }, [schools]);

  const glows = useMemo(() => {
    // pink pulse clusters at top-scoring school cities from Prom Cup
    return hotspots
      .filter((h) => h.city && wiCities[h.city])
      .map((h) => {
        const [lat, lng] = wiCities[h.city];
        const p = project(lat, lng);
        return { rank: h.rank, city: h.city, name: h.school_name, slug: h.school_slug, ...p };
      });
  }, [hotspots]);

  // live search filter (typing narrows to matching schools)
  useEffect(() => {
    if (!q.trim()) {
      setMatches([]);
      return;
    }
    const t = q.toLowerCase();
    setMatches(
      schools
        .filter((s) => [s.name, s.city, s.mascot].some((v) => (v || "").toLowerCase().includes(t)))
        .slice(0, 6)
    );
  }, [q, schools]);

  const submitSearch = () => {
    if (matches[0]) navigate(`/schools/${matches[0].slug || matches[0].id}`);
    else if (q.trim()) navigate(`/schools?q=${encodeURIComponent(q.trim())}`);
  };

  const fmtMoney = (n) => {
    if (n == null) return "$0";
    if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
    return `$${Math.round(n).toLocaleString()}`;
  };

  return (
    <div className="relative aspect-square max-w-lg mx-auto select-none" data-testid="wi-map">
      {/* Search box overlay */}
      <div className="absolute top-2 left-2 right-2 z-20">
        <div className="relative">
          <input
            data-testid="wi-map-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitSearch()}
            placeholder="Find your school…"
            className="w-full glass px-5 py-3 rounded-full font-body text-sm text-ink placeholder:text-ink/40 outline-none border border-rosemetal/20 shadow-sm focus:border-rosemetal transition-colors"
          />
          {matches.length > 0 && (
            <div className="absolute top-full left-2 right-2 mt-2 bg-ivory rounded-sm shadow-lg border border-border overflow-hidden z-30" data-testid="wi-map-suggestions">
              {matches.map((s) => (
                <button
                  key={s.id}
                  onClick={() => navigate(`/schools/${s.slug || s.id}`)}
                  className="w-full text-left px-4 py-2.5 hover:bg-blush/50 border-b border-border last:border-b-0 transition-colors"
                >
                  <div className="font-display text-sm">{s.name}</div>
                  <div className="font-label text-[9px] text-muted-foreground tracking-widest">{s.city}{s.mascot ? ` · ${s.mascot}` : ""}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <svg viewBox="0 0 500 500" className="w-full h-full" role="img" aria-label="Wisconsin school network map">
        <defs>
          <radialGradient id="wi-fill" cx="45%" cy="55%" r="65%">
            <stop offset="0%" stopColor="#fdf3ee" />
            <stop offset="70%" stopColor="#fbe6dd" />
            <stop offset="100%" stopColor="#f6d8cb" />
          </radialGradient>
          <radialGradient id="wi-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#e11d48" stopOpacity="0.45" />
            <stop offset="40%" stopColor="#f472b6" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#f472b6" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Real Wisconsin outline (mainland + Washington Island + Apostle Island) */}
        <path d={WI_PATH} fill="url(#wi-fill)" stroke="#e11d48" strokeWidth="1.1" strokeLinejoin="round" fillRule="evenodd" />

        {/* Pink glow hotspots — top Prom Cup schools */}
        {glows.map((g, i) => (
          <g key={g.slug + i}>
            <circle cx={g.x} cy={g.y} r="42" fill="url(#wi-glow)" />
            <motion.circle
              cx={g.x}
              cy={g.y}
              r={7}
              fill="#e11d48"
              stroke="#fff"
              strokeWidth="2.5"
              initial={{ scale: 0.6, opacity: 0.7 }}
              animate={{ scale: [0.9, 1.15, 0.9], opacity: [0.9, 1, 0.9] }}
              transition={{ duration: 2.5, delay: i * 0.4, repeat: Infinity, ease: "easeInOut" }}
            />
          </g>
        ))}

        {/* All individual schools as small dark dots */}
        {dots.map((d) => (
          <circle
            key={d.id}
            cx={d.x}
            cy={d.y}
            r="2.2"
            fill="#3a2a26"
            opacity="0.75"
            className="cursor-pointer hover:opacity-100"
            data-testid={`wi-dot-${d.id}`}
            onClick={() => navigate(`/schools/${d.slug || d.id}`)}
          >
            <title>{d.name} · {d.city}</title>
          </circle>
        ))}
      </svg>

      {/* Footer stat pills */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
        <div className="glass px-4 py-2 rounded-full font-label text-[10px] text-rosemetal tracking-widest shadow-sm">
          +{schoolsAddedToday} SCHOOLS TODAY
        </div>
        <div className="glass px-4 py-2 rounded-full font-label text-[10px] text-rosemetal tracking-widest shadow-sm">
          {fmtMoney(fundsRaisedWeek)} RAISED THIS WEEK
        </div>
      </div>
    </div>
  );
}
